<!-- UploadedReportsGallery.vue -->
<template>
  <transition name="fade">
    <div v-if="showlist" class="rg-mask" @click.self="close">
      <div class="rg-modal">
        <!-- Header -->
        <div class="rg-header">
          <template v-if="current">
            <button class="rg-btn" @click="closeViewer">← Gallery</button>
            <span class="rg-title rg-ellipsis">{{ current.report_actual_name }}</span>
            <span class="rg-count">{{ viewIndex + 1 }} / {{ files.length }}</span>
          </template>
          <template v-else>
            <span class="rg-title">Uploaded Reports</span>
            <span class="rg-count">{{ files.length }} {{ files.length === 1 ? 'file' : 'files' }}</span>
          </template>
          <button class="rg-x" aria-label="Close" @click="close">×</button>
        </div>

        <!-- Gallery grid -->
        <div v-if="!current" class="rg-body">
          <div v-if="!files.length" class="rg-empty">No files have been added so far.</div>
          <div v-else class="rg-grid">
            <div v-for="(f, i) in files" :key="f._key" class="rg-card">
              <div class="rg-thumb" @click="openViewer(i)">
                 <img v-if="f._type !== 'pdf' && urls[f._key] && !failed[f._key]" :src="urls[f._key]" alt=""
                  @error="$set(failed, f._key, true)" />
                <div v-else class="rg-fileicon">
                  <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                   <span>{{ f._type === 'pdf' ? 'PDF' : (urls[f._key] ? 'File' : '…') }}</span>
                </div>
              </div>
              <div class="rg-meta">
                <div class="rg-name rg-ellipsis" :title="f.report_actual_name">{{ f.report_actual_name }}</div>
                <div class="rg-date">{{ f.commondate }}</div>
                <button class="rg-view" @click="openViewer(i)">View</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Viewer -->
        <div v-else class="rg-viewer">
          <button class="rg-nav rg-prev" :disabled="viewIndex === 0" @click="go(-1)">‹</button>
          <div class="rg-stage">
            <div v-if="!urls[current._key] && !failed[current._key]" class="rg-empty">Loading…</div>
            <div v-else-if="failed[current._key] && current._type !== 'pdf'" class="rg-empty">
              Preview not available.
              <a v-if="urls[current._key]" :href="urls[current._key]" target="_blank" rel="noopener">Open in new tab</a>
            </div>
            <template v-else>
              <iframe v-if="current._type === 'pdf'" :src="urls[current._key]" class="rg-pdf"></iframe>
              <img v-else :src="urls[current._key]" alt="" @error="$set(failed, current._key, true)" />
              <div class="rg-info">
                <span>Timestamp: {{ current.commondate }}</span>
                <a v-if="current._type === 'pdf'" :href="urls[current._key]" target="_blank" rel="noopener">Open in new tab</a>
                <div v-if="featuresNoted"><strong>Signs/Features Noted</strong><p>{{ featuresNoted }}</p></div>
                <div v-if="inference"><strong>Inference</strong><p>{{ inference }}</p></div>
              </div>
            </template>
          </div>
          <button class="rg-nav rg-next" :disabled="viewIndex === files.length - 1" @click="go(1)">›</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import rest_service from '@/api/rest_service';
import Axios from 'axios';
import Auth from '@/common/mixins';

const fileUrlApi = '/Annotate/getannotatedfileurl/';
const findingsApi = '/Annotate/getexaminationfindings';

export default {
  mixins: [Auth],
  props: {
    showlist: Boolean,
    filelist: { type: Array, default: () => [] },
    startIndex: { type: Number, default: -1 } // >=0 opens that file directly
  },
  data() {
    return {
      tenant_id: '', authenticationToken: '', loggedin_user_unique_id: '', role_unique_id: '',
      urls: {}, failed: {}, viewIndex: -1,
      featuresNoted: '', inference: ''
    };
  },
  computed: {
    files() {
      return (this.filelist || []).map((f, i) =>
        Object.assign({}, f, { _type: this.resolveType(f), _key: i + ':' + f.unique_id }));
    },
    current() {
      return this.viewIndex >= 0 ? this.files[this.viewIndex] : null;
    }
  },
  created() {
    if (Auth.checkAuthentication()) {
      this.tenant_id = localStorage.tenantid;
      this.authenticationToken = localStorage.accesstoken;
      if (localStorage.loggedInUserInfo) {
        const u = JSON.parse(localStorage.loggedInUserInfo);
        this.loggedin_user_unique_id = u.unique_id;
        this.role_unique_id = u.role.unique_id;
      }
    }
  },
  watch: {
    showlist: {
      immediate: true,
      handler(open) {
        if (open) {
          document.body.classList.add('modal-open');
          window.addEventListener('keydown', this.onKey);
          this.urls = {}; this.failed = {}; // signed URLs expire, refetch each open
          this.viewIndex = -1;
          this.loadUrls().then(() => {
            if (this.startIndex >= 0) this.openViewer(this.startIndex);
          });
        } else {
          document.body.classList.remove('modal-open');
          window.removeEventListener('keydown', this.onKey);
        }
      }
    }
  },
  beforeDestroy() {
    document.body.classList.remove('modal-open');
    window.removeEventListener('keydown', this.onKey);
  },
  methods: {
    headers(route, extra) {
      return Object.assign({
        x_tenant_id: this.tenant_id,
        role_unique_id: this.role_unique_id,
        route_entity_name: route,
        loggedin_user_unique_id: this.loggedin_user_unique_id,
        Authorization: this.authenticationToken
      }, extra || {});
    },
    resolveType(f) {
      const ct = (f.contentType || '').toLowerCase();
      if (ct.indexOf('image/') === 0) return 'image';
      if (ct === 'application/pdf') return 'pdf';
      const n = (f.report_actual_name || '').toLowerCase();
      if (/\.(png|jpe?g|gif|webp|bmp)$/.test(n)) return 'image';
      if (/\.pdf$/.test(n)) return 'pdf';
      return 'unknown'; // octet-stream with no extension: try as image, fall back to open-in-tab
    },
    fileKey(f) {
      return f.category === 'Annotated'
        ? '0/' + f.unique_id
        : this.tenant_id + this.$store.state.helper.tenant_s3upload_Folders.UploadedImagePath + f.report_file_key;
    },
    loadUrls() {
      return Promise.all(this.files.map(f =>
        Axios.get(rest_service.baseURL + fileUrlApi + this.fileKey(f), {
          headers: this.headers('/Annotate')
        }).then(res => {
          if (res && res.data && res.data.result) this.$set(this.urls, f._key, res.data.result);
          else this.$set(this.failed, f._key, true);
        }).catch(() => this.$set(this.failed, f._key, true))
      ));
    },
    openViewer(i) {
      this.viewIndex = i;
      this.featuresNoted = ''; this.inference = '';
      const f = this.files[i];
      if (!f || f.category !== 'Annotated' || f._type === 'pdf') return;
      const key = f._key;
      Axios.get(rest_service.baseURL + findingsApi + '?unique_id=' + f.unique_id, {
        headers: this.headers('/annotate')
      }).then(xy => {
        if (!this.current || this.current._key !== key) return;// user already moved on
        const r = xy.data && xy.data.result && xy.data.result[0];
        if (r) { this.featuresNoted = r.featuresNoted || ''; this.inference = r.inference || ''; }
      }).catch(c => console.error('getexaminationfindings failed', c));
    },
    closeViewer() { this.viewIndex = -1; },
    go(step) {
      const n = this.viewIndex + step;
      if (n >= 0 && n < this.files.length) this.openViewer(n);
    },
    onKey(e) {
      if (e.key === 'Escape') return this.current ? this.closeViewer() : this.close();
      if (this.current && e.key === 'ArrowLeft') this.go(-1);
      if (this.current && e.key === 'ArrowRight') this.go(1);
    },
    close() {
      this.viewIndex = -1;
      this.$emit('close');
      if (this.$parent && this.$parent.showuploadedfilelist) this.$parent.showuploadedfilelist = false;
    }
  }
};
</script>

<style scoped>
.rg-mask { position: fixed; inset: 0; z-index: 9998; background: rgba(0,0,0,.7); display: flex; align-items: center; justify-content: center; }
.rg-modal { width: min(960px, 94vw); max-height: 90vh; background: #fff; border-radius: 10px; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 8px 30px rgba(0,0,0,.35); }
.rg-header { background: #0b63e5; color: #fff; display: flex; align-items: center; gap: 12px; padding: 14px 18px; }
.rg-title { font-weight: 600; font-size: 16px; flex: 1; }
.rg-count { font-size: 12px; opacity: .85; }
.rg-x { background: rgba(255,255,255,.2); border: 0; color: #fff; width: 30px; height: 30px; border-radius: 6px; font-size: 20px; line-height: 1; cursor: pointer; }
.rg-btn { background: rgba(255,255,255,.2); border: 0; color: #fff; padding: 5px 10px; border-radius: 6px; cursor: pointer; }
.rg-body { padding: 18px; overflow-y: auto; }
.rg-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 16px; }
.rg-card { border: 1px solid #e3e8ef; border-radius: 8px; overflow: hidden; background: #fff; }
.rg-thumb { height: 150px; background: #f3f6fa; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.rg-thumb img { width: 100%; height: 100%; object-fit: cover; }
.rg-fileicon { display: flex; flex-direction: column; align-items: center; gap: 4px; color: #6b7a90; font-size: 12px; font-weight: 600; }
.rg-meta { padding: 10px 12px; }
.rg-name { font-weight: 600; font-size: 13px; }
.rg-date { font-size: 12px; color: #6b7a90; margin: 2px 0 8px; }
.rg-view { width: 100%; background: #eaf2ff; color: #0b63e5; border: 1px solid #c7dcff; border-radius: 6px; padding: 5px 0; cursor: pointer; }
.rg-ellipsis { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rg-empty { text-align: center; padding: 40px 0; color: #6b7a90; }
.rg-viewer { display: flex; align-items: stretch; min-height: 0; flex: 1; }
.rg-stage { flex: 1; overflow-y: auto; padding: 16px; text-align: center; max-height: calc(90vh - 60px); }
.rg-stage img { max-width: 100%; max-height: 65vh; object-fit: contain; }
.rg-pdf { width: 100%; height: 70vh; border: 0; }
.rg-info { text-align: left; font-size: 14px; margin-top: 12px; }
.rg-info p { margin: 2px 0 10px; }
.rg-nav { width: 44px; border: 0; background: #f3f6fa; font-size: 28px; cursor: pointer; }
.rg-nav:disabled { opacity: .35; cursor: default; }
.fade-enter-active, .fade-leave-active { transition: opacity .2s; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>